/**
 * The guide's own microcopy — buttons, notices, captions — in every language a guide is written in.
 *
 * The guides translate their prose per field; this is the frame around that prose. Without it a
 * reader who switched to German would read a German step under an English "Next" button, which
 * looks like the setting only half took. One object per language, all keys required: unlike a
 * guide, the frame is small enough to be complete, and the type makes a missing key a compile
 * error rather than an English word in the middle of a Serbian card.
 *
 * Pure data like `types.ts`, so the manual generator can import it with no UI around it.
 */
import type { HelpLanguage } from './types';

export interface HelpUiText {
  // launcher
  launcherTooltip: (page: string) => string;
  launcherAria: (page: string) => string;
  floatingAria: string;
  floatingHide: string;
  floatingHideAria: string;
  floatingHidden: string;
  undo: string;
  tourEmptyDialog: string;
  tourEmptyPage: string;
  tourStepHidden: (title: string, hint: string) => string;
  tourStepOffScreen: (title: string) => string;
  // drawer
  eyebrow: string;
  close: string;
  languageAria: string;
  showMeAround: string;
  pointAtField: string;
  drawerHint: string;
  searchPlaceholder: (count: number) => string;
  searchAria: string;
  notOnScreen: string;
  locateTooltip: string;
  locateAria: (title: string) => string;
  startHereTooltip: string;
  startHereAria: (title: string) => string;
  matchLine: (matches: number, total: number, query: string) => string;
  nothingMatches: string;
  goodToKnow: string;
  moreSection: string;
  /** Pill on a card whose text fell back to the English source. */
  fallbackPill: string;
  floatingLabel: string;
  floatingSwitchAria: string;
  floatingOn: string;
  floatingOff: string;
  // tour
  tourAria: (step: number, total: number, title: string) => string;
  tourCounter: (step: number, total: number) => string;
  tourJumpAria: (step: number, total: number) => string;
  previousSection: string;
  nextSection: string;
  endTour: string;
  back: string;
  next: string;
  done: string;
  // step jump
  jumpPlaceholder: string;
  jumpAria: string;
  jumpEmpty: string;
  jumpFooter: (matches: number, total: number) => string;
  // inspector
  inspectBanner: string;
  inspectEsc: string;
  inspectLeaveAria: string;
  inspectMiss: string;
  inspectCardAria: (title: string) => string;
  continueFromHere: string;
  // printed manual
  manualTitle: string;
  manualGenerated: (date: string) => string;
  manualContents: string;
  manualToSeeThis: string;
  manualOptional: string;
}

const en: HelpUiText = {
  launcherTooltip: (page) => `Page guide: ${page}`,
  launcherAria: (page) => `Open the page guide for ${page}`,
  floatingAria: 'Open the page guide (floating shortcut)',
  floatingHide: 'Hide this — the guide stays in the top bar',
  floatingHideAria: 'Hide the floating page-guide button',
  floatingHidden: 'Floating button hidden. The guide is still one click away: the “?” in the top bar.',
  undo: 'Undo',
  tourEmptyDialog:
    'Nothing in this dialog is documented yet — close it to tour the page, or read the panel.',
  tourEmptyPage: 'Nothing to point at on this screen yet — the guide in the panel covers it all.',
  tourStepHidden: (title, hint) => `“${title}” is not on screen yet. ${hint}`,
  tourStepOffScreen: (title) => `“${title}” is not on screen right now, so the tour cannot open on it.`,
  eyebrow: 'Page guide',
  close: 'Close page guide',
  languageAria: 'Guide language',
  showMeAround: 'Show me around',
  pointAtField: 'Point at a field',
  drawerHint:
    'The tour visits what is on screen right now — or only the open dialog, when there is one; its step counter jumps to any of them. “Point at a field” answers the other way round: click the control you are looking at and read what it does.',
  searchPlaceholder: (count) => `Search all ${count} entries…`,
  searchAria: 'Search this page’s guide',
  notOnScreen: 'Not on screen right now.',
  locateTooltip: 'Show me where this is',
  locateAria: (title) => `Locate: ${title}`,
  startHereTooltip: 'Walk me through from here',
  startHereAria: (title) => `Start the tour at: ${title}`,
  matchLine: (matches, total, query) => `${matches} of ${total} entries match “${query}”.`,
  nothingMatches: 'Nothing on this page matches.',
  goodToKnow: 'Good to know',
  moreSection: 'More',
  fallbackPill: 'EN',
  floatingLabel: 'Floating “?” button',
  floatingSwitchAria: 'Show a floating page-guide button',
  floatingOn:
    'Shown in the bottom-right corner, where it can cover a table’s pagination and row buttons.',
  floatingOff:
    'Off — the “?” in the top bar opens this guide, and stays clickable while a dialog is open.',
  tourAria: (step, total, title) => `Tour step ${step} of ${total}: ${title}`,
  tourCounter: (step, total) => `STEP ${step} OF ${total}`,
  tourJumpAria: (step, total) => `Step ${step} of ${total} — jump to another step`,
  previousSection: 'Previous section',
  nextSection: 'Next section',
  endTour: 'End tour',
  back: 'Back',
  next: 'Next',
  done: 'Done',
  jumpPlaceholder: 'Search this page’s guide…',
  jumpAria: 'Search the steps on this page',
  jumpEmpty:
    'Nothing on this screen matches. The panel’s “This page” list includes the parts that are not on screen right now too.',
  jumpFooter: (matches, total) => `${matches} of ${total} · Enter goes to the first`,
  inspectBanner: 'Click any field to read what it does',
  inspectEsc: 'Esc to leave',
  inspectLeaveAria: 'Leave point-at-a-field mode',
  inspectMiss: 'No guide entry for this control yet — try the field itself, or the page guide panel.',
  inspectCardAria: (title) => `What this does: ${title}`,
  continueFromHere: 'Continue from here',
  manualTitle: 'User manual',
  manualGenerated: (date) => `Generated ${date} from the in-app page guides.`,
  manualContents: 'Contents',
  manualToSeeThis: 'To see this:',
  manualOptional: 'Shown only when there is data for it.',
};

const deAT: HelpUiText = {
  launcherTooltip: (page) => `Seitenhilfe: ${page}`,
  launcherAria: (page) => `Seitenhilfe für ${page} öffnen`,
  floatingAria: 'Seitenhilfe öffnen (schwebende Schaltfläche)',
  floatingHide: 'Ausblenden — die Hilfe bleibt in der oberen Leiste',
  floatingHideAria: 'Schwebende Hilfe-Schaltfläche ausblenden',
  floatingHidden:
    'Schwebende Schaltfläche ausgeblendet. Die Hilfe ist weiterhin einen Klick entfernt: das „?“ in der oberen Leiste.',
  undo: 'Rückgängig',
  tourEmptyDialog:
    'In diesem Dialog ist noch nichts beschrieben — schließen Sie ihn für eine Tour durch die Seite, oder lesen Sie das Hilfe-Panel.',
  tourEmptyPage:
    'Auf diesem Bildschirm gibt es noch nichts zu zeigen — die Hilfe im Panel beschreibt alles.',
  tourStepHidden: (title, hint) => `„${title}“ ist noch nicht sichtbar. ${hint}`,
  tourStepOffScreen: (title) =>
    `„${title}“ ist gerade nicht sichtbar, daher kann die Tour dort nicht beginnen.`,
  eyebrow: 'Seitenhilfe',
  close: 'Seitenhilfe schließen',
  languageAria: 'Sprache der Hilfe',
  showMeAround: 'Tour starten',
  pointAtField: 'Auf ein Feld zeigen',
  drawerHint:
    'Die Tour zeigt, was gerade am Bildschirm ist — oder nur den offenen Dialog, falls einer offen ist; über den Schrittzähler springen Sie zu jedem Schritt. „Auf ein Feld zeigen“ geht den umgekehrten Weg: Klicken Sie auf das Element, das Sie sehen, und lesen Sie, was es tut.',
  searchPlaceholder: (count) => `Alle ${count} Einträge durchsuchen…`,
  searchAria: 'Hilfe dieser Seite durchsuchen',
  notOnScreen: 'Gerade nicht am Bildschirm.',
  locateTooltip: 'Zeigen, wo das ist',
  locateAria: (title) => `Anzeigen: ${title}`,
  startHereTooltip: 'Tour ab hier',
  startHereAria: (title) => `Tour beginnen bei: ${title}`,
  matchLine: (matches, total, query) => `${matches} von ${total} Einträgen passen zu „${query}“.`,
  nothingMatches: 'Nichts auf dieser Seite passt.',
  goodToKnow: 'Gut zu wissen',
  moreSection: 'Weiteres',
  fallbackPill: 'EN',
  floatingLabel: 'Schwebende „?“-Schaltfläche',
  floatingSwitchAria: 'Schwebende Hilfe-Schaltfläche anzeigen',
  floatingOn:
    'Unten rechts eingeblendet, wo sie die Seitennavigation und Zeilenaktionen einer Tabelle verdecken kann.',
  floatingOff:
    'Aus — das „?“ in der oberen Leiste öffnet diese Hilfe und bleibt auch bei offenem Dialog anklickbar.',
  tourAria: (step, total, title) => `Tour-Schritt ${step} von ${total}: ${title}`,
  tourCounter: (step, total) => `SCHRITT ${step} VON ${total}`,
  tourJumpAria: (step, total) => `Schritt ${step} von ${total} — zu einem anderen Schritt springen`,
  previousSection: 'Vorheriger Abschnitt',
  nextSection: 'Nächster Abschnitt',
  endTour: 'Tour beenden',
  back: 'Zurück',
  next: 'Weiter',
  done: 'Fertig',
  jumpPlaceholder: 'Hilfe dieser Seite durchsuchen…',
  jumpAria: 'Schritte auf dieser Seite durchsuchen',
  jumpEmpty:
    'Nichts auf diesem Bildschirm passt. Die Liste im Hilfe-Panel enthält auch die Teile, die gerade nicht sichtbar sind.',
  jumpFooter: (matches, total) => `${matches} von ${total} · Eingabetaste springt zum ersten`,
  inspectBanner: 'Klicken Sie auf ein Feld, um zu lesen, was es tut',
  inspectEsc: 'Esc zum Beenden',
  inspectLeaveAria: 'Modus „Auf ein Feld zeigen“ beenden',
  inspectMiss:
    'Für dieses Element gibt es noch keinen Hilfeeintrag — versuchen Sie das Feld selbst oder das Hilfe-Panel.',
  inspectCardAria: (title) => `Was das tut: ${title}`,
  continueFromHere: 'Ab hier weiter',
  manualTitle: 'Benutzerhandbuch',
  manualGenerated: (date) => `Erstellt am ${date} aus den Seitenhilfen der Anwendung.`,
  manualContents: 'Inhalt',
  manualToSeeThis: 'So wird es sichtbar:',
  manualOptional: 'Nur sichtbar, wenn Daten dafür vorhanden sind.',
};

const srLatn: HelpUiText = {
  launcherTooltip: (page) => `Vodič za stranicu: ${page}`,
  launcherAria: (page) => `Otvori vodič za stranicu ${page}`,
  floatingAria: 'Otvori vodič za stranicu (plutajuće dugme)',
  floatingHide: 'Sakrij — vodič ostaje u gornjoj traci',
  floatingHideAria: 'Sakrij plutajuće dugme vodiča',
  floatingHidden: 'Plutajuće dugme je sakriveno. Vodič je i dalje na jedan klik: „?“ u gornjoj traci.',
  undo: 'Poništi',
  tourEmptyDialog:
    'Ništa u ovom dijalogu još nije opisano — zatvorite ga za obilazak stranice, ili pročitajte panel.',
  tourEmptyPage: 'Na ovom ekranu još nema šta da se pokaže — vodič u panelu opisuje sve.',
  tourStepHidden: (title, hint) => `„${title}“ još nije na ekranu. ${hint}`,
  tourStepOffScreen: (title) =>
    `„${title}“ trenutno nije na ekranu, pa obilazak ne može da počne od njega.`,
  eyebrow: 'Vodič za stranicu',
  close: 'Zatvori vodič',
  languageAria: 'Jezik vodiča',
  showMeAround: 'Provedi me',
  pointAtField: 'Pokaži na polje',
  drawerHint:
    'Obilazak prolazi kroz ono što je trenutno na ekranu — ili samo kroz otvoreni dijalog, ako ga ima; brojač koraka skače na bilo koji od njih. „Pokaži na polje“ radi obrnuto: kliknite na kontrolu koju gledate i pročitajte šta radi.',
  searchPlaceholder: (count) => `Pretraži svih ${count} stavki…`,
  searchAria: 'Pretraži vodič ove stranice',
  notOnScreen: 'Trenutno nije na ekranu.',
  locateTooltip: 'Pokaži mi gde je',
  locateAria: (title) => `Pronađi: ${title}`,
  startHereTooltip: 'Provedi me odavde',
  startHereAria: (title) => `Počni obilazak od: ${title}`,
  matchLine: (matches, total, query) => `${matches} od ${total} stavki odgovara upitu „${query}“.`,
  nothingMatches: 'Ništa na ovoj stranici ne odgovara.',
  goodToKnow: 'Dobro je znati',
  moreSection: 'Ostalo',
  fallbackPill: 'EN',
  floatingLabel: 'Plutajuće „?“ dugme',
  floatingSwitchAria: 'Prikaži plutajuće dugme vodiča',
  floatingOn: 'Prikazano u donjem desnom uglu, gde može da prekrije paginaciju i dugmad u redovima tabele.',
  floatingOff:
    'Isključeno — „?“ u gornjoj traci otvara ovaj vodič i ostaje klikabilan i dok je dijalog otvoren.',
  tourAria: (step, total, title) => `Korak obilaska ${step} od ${total}: ${title}`,
  tourCounter: (step, total) => `KORAK ${step} OD ${total}`,
  tourJumpAria: (step, total) => `Korak ${step} od ${total} — skoči na drugi korak`,
  previousSection: 'Prethodna sekcija',
  nextSection: 'Sledeća sekcija',
  endTour: 'Završi obilazak',
  back: 'Nazad',
  next: 'Dalje',
  done: 'Gotovo',
  jumpPlaceholder: 'Pretraži vodič ove stranice…',
  jumpAria: 'Pretraži korake na ovoj stranici',
  jumpEmpty:
    'Ništa na ovom ekranu ne odgovara. Lista u panelu vodiča sadrži i delove koji trenutno nisu na ekranu.',
  jumpFooter: (matches, total) => `${matches} od ${total} · Enter skače na prvi`,
  inspectBanner: 'Kliknite na bilo koje polje da pročitate šta radi',
  inspectEsc: 'Esc za izlaz',
  inspectLeaveAria: 'Izađi iz režima „Pokaži na polje“',
  inspectMiss: 'Za ovu kontrolu još nema stavke u vodiču — probajte samo polje, ili panel vodiča.',
  inspectCardAria: (title) => `Šta ovo radi: ${title}`,
  continueFromHere: 'Nastavi odavde',
  manualTitle: 'Korisničko uputstvo',
  manualGenerated: (date) => `Generisano ${date} iz vodiča u aplikaciji.`,
  manualContents: 'Sadržaj',
  manualToSeeThis: 'Da biste ovo videli:',
  manualOptional: 'Prikazuje se samo kada za to postoje podaci.',
};

export const HELP_UI_TEXT: Record<HelpLanguage, HelpUiText> = {
  en,
  'de-AT': deAT,
  'sr-Latn-RS': srLatn,
};

export const uiText = (language: HelpLanguage): HelpUiText => HELP_UI_TEXT[language];
